import React, { useMemo } from 'react'
import { Spinner } from '@components/Spinner'
import { Box, FancyButton, Paragraph, Stack, Text } from '@ui'
import type { MiniappEmbed } from 'api/lib/unfurl'
import { AnimatePresence, motion } from 'framer-motion'

type MiniappSplashState = {
    missingUrl: boolean
    invalidOrigin: boolean
    iframeError: string | null
    iframeLoaded: boolean
    embedLoading: boolean
    fallbackActive: boolean
    sdkLoading: boolean
}

type MiniappSplashProps = {
    embed: MiniappEmbed | null
    miniappUrl?: string
    state: MiniappSplashState
    onFallbackContinue?: () => void
    onRetry?: () => void
}

const getHostFromUrl = (url?: string) => {
    if (!url) {
        return null
    }
    try {
        const parsed = new URL(url)
        return parsed.host
    } catch (error) {
        console.warn('[MiniappSplash] Failed to parse miniapp URL', error)
        return null
    }
}

export const MiniappSplash = ({
    embed,
    miniappUrl,
    state,
    onFallbackContinue,
    onRetry,
}: MiniappSplashProps): React.ReactElement => {
    const host = useMemo(() => getHostFromUrl(miniappUrl), [miniappUrl])

    const title = useMemo(() => {
        const actionName = embed?.button?.action?.name?.trim()
        if (actionName) {
            return actionName
        }
        const buttonTitle = embed?.button?.title?.trim()
        if (buttonTitle) {
            return buttonTitle
        }
        return host ?? 'Miniapp'
    }, [embed, host])

    const ctaLabel = embed?.button?.title?.trim() || 'Continue'
    const action = embed?.button?.action
    const previewImageUrl = action?.splashImageUrl || embed?.imageUrl
    const splashBackground = action?.splashBackgroundColor
    const derivedState = useMemo(() => {
        if (state.missingUrl) {
            return {
                status: 'error' as const,
                message: 'Miniapp URL is unavailable.',
                canRetry: false,
            }
        }
        if (state.invalidOrigin) {
            return {
                status: 'error' as const,
                message: 'Miniapp URL is invalid.',
                canRetry: false,
            }
        }
        if (state.iframeError) {
            return {
                status: 'error' as const,
                message: state.iframeError,
                canRetry: true,
            }
        }
        // Only show fallback if SDK is ready and iframe has loaded
        // Otherwise keep showing loading state
        if (state.fallbackActive && !state.sdkLoading && state.iframeLoaded) {
            return { status: 'fallback' as const }
        }
        return { status: 'loading' as const }
    }, [state])

    const isLoading = derivedState.status === 'loading'
    const isError = derivedState.status === 'error'
    const isFallback = derivedState.status === 'fallback'
    const canRetry = derivedState.status === 'error' && derivedState.canRetry && !!onRetry
    const loadingMessage = useMemo(() => {
        if (state.sdkLoading) {
            return 'Preparing…'
        }
        if (!state.iframeLoaded) {
            return 'Connecting to miniapp…'
        }
        if (state.embedLoading) {
            return 'Preparing preview…'
        }
        return 'Waiting for miniapp to respond…'
    }, [state.sdkLoading, state.embedLoading, state.iframeLoaded])

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ position: 'absolute', inset: 0, zIndex: 10 }}
            >
                <Box
                    position="absolute"
                    top="none"
                    left="none"
                    width="100%"
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{
                        inset: 0,
                        backdropFilter: 'blur(14px) saturate(120%)',
                        backgroundColor: 'rgba(8, 10, 20, 0.88)',
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        style={{ width: '100%', maxWidth: '420px' }}
                    >
                        <Stack
                            width="100%"
                            padding="lg"
                            gap="lg"
                            background="level2"
                            rounded="lg"
                            border="faint"
                            alignItems="center"
                            textAlign="center"
                        >
                            <Box
                                width="100%"
                                overflow="hidden"
                                rounded="lg"
                                border="level3"
                                style={{
                                    backgroundColor: splashBackground || undefined,
                                }}
                            >
                                {previewImageUrl ? (
                                    <Box
                                        as="img"
                                        src={previewImageUrl}
                                        alt={title}
                                        width="100%"
                                        height="auto"
                                        objectFit="cover"
                                    />
                                ) : (
                                    <Box
                                        centerContent
                                        width="100%"
                                        background="level3"
                                        height="200"
                                        borderRadius="lg"
                                    >
                                        <Text color="gray2">Miniapp preview</Text>
                                    </Box>
                                )}
                            </Box>
                            <Stack gap="sm" alignItems="center">
                                <Paragraph strong size="lg" color="default">
                                    {title}
                                </Paragraph>
                            </Stack>
                            {isLoading ? (
                                <Stack gap="xs" alignItems="center">
                                    <Spinner />
                                    <Text size="sm" color="gray2">
                                        {loadingMessage}
                                    </Text>
                                </Stack>
                            ) : isError ? (
                                <Stack gap="sm" alignItems="center">
                                    <Text size="sm" color="gray2">
                                        {derivedState.message ??
                                            'We could not load this miniapp. Please try again.'}
                                    </Text>
                                    {canRetry && (
                                        <FancyButton
                                            width="100%"
                                            borderRadius="sm"
                                            aria-label="Retry loading miniapp"
                                            onClick={onRetry}
                                        >
                                            Try again
                                        </FancyButton>
                                    )}
                                </Stack>
                            ) : isFallback ? (
                                <Stack gap="sm" alignItems="center">
                                    {onFallbackContinue && (
                                        <FancyButton
                                            cta
                                            width="100%"
                                            borderRadius="sm"
                                            aria-label="Continue to miniapp"
                                            onClick={onFallbackContinue}
                                        >
                                            {ctaLabel}
                                        </FancyButton>
                                    )}
                                </Stack>
                            ) : null}
                        </Stack>
                    </motion.div>
                </Box>
            </motion.div>
        </AnimatePresence>
    )
}
